import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_Row,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  type MRT_PaginationState,
  MRT_ColumnDef,
} from 'material-react-table';
import {Box, Button, Dialog, DialogContent, DialogTitle, Typography, Checkbox, CircularProgress, TextField} from '@mui/material';
import {
    useCreatePrevServElem,
    useDeletePrevServElem,
    useGetServElemDTOByPrev
} from "./ServicesSE";
import { PrevTestaForm, ServElemDTO } from "./types";
import React, { useEffect, useMemo, useState } from "react";



interface PreventiviSEProps {
  onClose: () => void;
  idprevtesta: number;
}

const PreventiviSE: React.FC<PreventiviSEProps> = ({ onClose, idprevtesta }) => {
  interface FlagSECheckboxProps {
    row: MRT_Row<ServElemDTO>;
  }

  const FlagSECheckbox: React.FC<FlagSECheckboxProps> = ({ row }) => {
    const [checked, setChecked] = useState(row.original.flgse === 'Y');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.checked ? 'Y' : 'N';
      setChecked(e.target.checked);
      row.original.flgse = newValue;
    };

    return (
      <Checkbox
        checked={checked}
        onChange={handleChange}
      />
    );
  };



  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 1000 });
  const [saving, setSaving] = useState(false);


  const [initialRowsWithFlagSE, setInitialRowsWithFlagSE] = useState<ServElemDTO[]>([]);

  const { mutateAsync: createPrevServElem, isPending: isCreatingPrevServElem } = useCreatePrevServElem();
  const { mutateAsync: deletePrevServElem, isPending: isDeletingPrevServElem } = useDeletePrevServElem();

  const { data: { results = [], count } = {}, isError: isLoadingServElemDTOByPrevError, isFetching: isFetchingServElemDTOByPrev, isLoading: isLoadingServElemDTOByPrev } = useGetServElemDTOByPrev(pagination, columnFilters, globalFilter, sorting, idprevtesta);

  useEffect(() => {
    setInitialRowsWithFlagSE(results.filter((row) => row.flgse === 'Y'));
  }, [results]);

  const handleSaveSelected = async () => {
  setSaving(true);

  try {
    // console.log("initialRowsWithFlagSE", initialRowsWithFlagSE);

    const rowsWithFlagSE = results.filter((row) => row.flgse === 'Y');

    // console.log("rowsWithFlagSE", rowsWithFlagSE);

    const rowsToDelete = initialRowsWithFlagSE.filter(
      row => !rowsWithFlagSE.some(rowsWithFlagSE => rowsWithFlagSE.idservelem === row.idservelem)
    );

    // Rows that need to be saved (new and existing ones with potential updates)
    const rowsToSave = rowsWithFlagSE.filter(
      row => !initialRowsWithFlagSE.some(initialRow => initialRow.idservelem === row.idservelem)
    );



    // console.log("rowsToDelete", rowsToDelete);
    // console.log("rowsToSave", rowsToSave);


    for (const row of rowsToDelete) {
      await deletePrevServElem(row.idprevservelem);
    }

    for (const row of rowsToSave) {

        // console.log("rowstosave",row);

      const myPrevServElem = {
        idprevtesta: idprevtesta,
        idservelem: row.idservelem,
        note: row.note,
        numprep: row.numprep,
        numanalisi: row.numanalisi,
        numconf: row.numconf,
        numform: row.numform,
        numtot: row.numtot,

      };
      await createPrevServElem(myPrevServElem);
    }


  } catch (error) {
    console.error('Error saving selected rows:', error);
  } finally {
    setSaving(false);
    onClose();
  }
};


  const columns = useMemo<MRT_ColumnDef<ServElemDTO>[]>(
    () => [
      {
        accessorKey: 'flgse',
        header: '',
        Cell: ({ row }: { row: MRT_Row<ServElemDTO> }) => <FlagSECheckbox row={row} />,
        enableEditing: true,
        size: 60,
      },

      {
        accessorKey: 'desarea',
        header: 'Area',
        size: 180,
      },
      {
        accessorKey: 'desmodulo',
        header: 'Modulo',
        size: 200,
      },
      {
        accessorKey: 'deslivello',
        header: 'Livello',
        size: 90,
      },
      {
        accessorKey: 'desservelem',
        header: 'Serv.Elem.',
        size: 500,
          Cell: ({ cell }) => (
          <Typography variant="body2" style={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'numanalisi',
        header: 'Analisi',
        size: 80,
      },
      {
        accessorKey: 'numconf',
        header: 'Conf',
        size: 80,
      },
      {
        accessorKey: 'numform',
        header: 'Form',
        size: 80,
      },
      {
        accessorKey: 'numtot',
        header: 'Tot',
        size: 80,
      },
      {
        accessorKey: 'numprep',
        header: 'Prep',
        size: 80,
      },
    ],
    [validationErrors],
  );

  const table = useMaterialReactTable({
    columns,
    data: results,
    initialState: {      density: 'compact',      pagination: { pageSize: 1000, pageIndex: 0 },    },

    manualFiltering: true, //turn off built-in client-side filtering
    manualPagination: true, //turn off built-in client-side pagination
    manualSorting: true, //turn off built-in client-side sorting

    layoutMode: 'grid-no-grow',

    enableRowSelection: false,
    enableEditing: false,
    editDisplayMode: 'cell',
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',

    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,



    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          padding: '8px',
          flexWrap: 'wrap',
        }}
      >
        <Button variant="contained" onClick={handleSaveSelected} disabled={saving}>Save SE</Button>
        <Button variant="contained" onClick={() => onClose()} disabled={saving}>Chiudi</Button>
      </Box>
    ),

    state: {
      isLoading: isLoadingServElemDTOByPrev,
      // isSaving: isCreatingPrevTesta || isUpdatingPrevTesta || isDeletingPrevTesta,
     // isSaving:  isDeletingServElemDTOByPrev,
      showAlertBanner: isLoadingServElemDTOByPrevError,
      showProgressBars: isFetchingServElemDTOByPrev,

      columnFilters,
      globalFilter,
      pagination,
      sorting,
    },

  });



  return (
    <Dialog onClose={onClose} open fullWidth={true} maxWidth={"xl"}>
      <DialogTitle>
        <Typography variant="h5">Service Elements Preventivo {idprevtesta} </Typography>
      </DialogTitle>
      <DialogContent>
        {saving ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="50%">
            <CircularProgress />
          </Box>
        ) : (
          <MaterialReactTable table={table} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PreventiviSE;

const validateNumber = (value: any): boolean => {
    // Check if the value is of type 'number' and is not NaN
    if (typeof value === 'number' && !isNaN(value)) {
        return true;
    }

    // If the value is a string, try to convert it to a number
    if (typeof value === 'string') {
        // Remove any commas in the string and replace with dots for proper parsing
        const normalizedValue = value.replace(',', '.');
        const parsedNumber = parseFloat(normalizedValue);
        return !isNaN(parsedNumber);
    }

    // If the value is neither a number nor a string, it's not valid
    return false;
};
